/* The as-of calendar popover (R3b, the ruled mock asof-picker.png).
 *
 * A small custom calendar under the header's date button — never the
 * browser's native input, which prints dd/mm/yyyy and knows nothing of
 * the record. Every rendered date is the officer's canon ("31 May 2026");
 * ISO lives only in state and test ids. On each day: a GOLD dot when a
 * balance was stated that day (a statement's date), a BLUE dot when a
 * movement is dated there — the objects' own colours, as marks. Future
 * days are greyed. "Jump to" chips list every date the record holds, a
 * balance date gold-outlined; "today" is the door home.
 *
 * Keyboard: arrows move the focused day, Enter picks, Esc closes. */

import { useEffect, useRef, useState } from 'react'
import { formatOfficerDate } from '../../lib/officerFormat'
import type { HistoryDate } from './rebuildRecord'
import { monthGrid, monthLabel, monthOf, shiftMonth } from './asOf'

interface Props {
  /** The as-of date in force (ISO), null while on today. */
  value: string | null
  today: string
  dates: HistoryDate[]
  onPick: (iso: string) => void
  onToday: () => void
  onClose: () => void
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

/** "5 Mar" within the current year, "5 Mar 2025" outside it. */
function shortDate(iso: string, today: string): string {
  const full = formatOfficerDate(iso) ?? iso
  return iso.slice(0, 4) === today.slice(0, 4) ? full.replace(/ \d{4}$/, '') : full
}

export default function AsOfPicker({ value, today, dates, onPick, onToday, onClose }: Props) {
  const latest = dates.length ? dates[dates.length - 1].date : null
  const [view, setView] = useState<string>(monthOf(value ?? latest ?? today))
  const gridRef = useRef<HTMLDivElement>(null)
  const byDate = new Map(dates.map((d) => [d.date, d]))
  const weeks = monthGrid(view)

  /* Focus lands on the chosen day (or the first day of the month) so the
   * arrows work at once; Esc closes from anywhere inside. */
  useEffect(() => {
    const root = gridRef.current
    if (!root) return
    const chosen = root.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')
    const first = root.querySelector<HTMLButtonElement>('button.in:not(:disabled)')
    ;(chosen ?? first)?.focus()
  }, [view])

  const moveFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const delta =
      e.key === 'ArrowLeft'
        ? -1
        : e.key === 'ArrowRight'
          ? 1
          : e.key === 'ArrowUp'
            ? -7
            : e.key === 'ArrowDown'
              ? 7
              : 0
    if (!delta) return
    const from = (e.target as HTMLElement).getAttribute('data-iso')
    if (!from) return
    e.preventDefault()
    const dt = new Date(`${from}T00:00:00Z`)
    dt.setUTCDate(dt.getUTCDate() + delta)
    const iso = dt.toISOString().slice(0, 10)
    const target = gridRef.current?.querySelector<HTMLButtonElement>(`button[data-iso="${iso}"]`)
    if (target && !target.disabled) target.focus()
    else if (monthOf(iso) !== view && iso <= today) setView(monthOf(iso))
  }

  return (
    <div
      className="fa-asof-pop"
      role="dialog"
      aria-label="the as-of date"
      data-testid="rebuild-asof-controls"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation()
          onClose()
        }
      }}
    >
      <div className="fa-cal-head">
        <button
          type="button"
          className="fa-cal-nav"
          onClick={() => setView(shiftMonth(view, -1))}
          aria-label="the month before"
          data-testid="rebuild-asof-month-prev"
        >
          ‹
        </button>
        <span className="fa-cal-month" data-testid="rebuild-asof-month">
          {monthLabel(view)}
        </span>
        <button
          type="button"
          className="fa-cal-nav"
          onClick={() => setView(shiftMonth(view, 1))}
          disabled={view >= monthOf(today)}
          aria-label="the month after"
          data-testid="rebuild-asof-month-next"
        >
          ›
        </button>
      </div>
      <div className="fa-cal-grid" ref={gridRef} onKeyDown={moveFocus} role="grid">
        {WEEKDAYS.map((w) => (
          <span className="fa-cal-wd" key={w} role="columnheader">
            {w}
          </span>
        ))}
        {weeks.flat().map((d) => {
          const h = byDate.get(d.iso)
          const future = d.iso > today
          const sel = value === d.iso
          return (
            <button
              type="button"
              key={d.iso}
              className={`fa-cal-day${d.inMonth ? ' in' : ' out'}${sel ? ' sel' : ''}${d.iso === today ? ' now' : ''}`}
              disabled={future || !d.inMonth}
              aria-pressed={sel ? true : undefined}
              aria-label={formatOfficerDate(d.iso) ?? d.iso}
              data-iso={d.iso}
              data-testid={d.inMonth ? `rebuild-asof-day-${d.iso}` : undefined}
              onClick={() => (d.iso === today ? onToday() : onPick(d.iso))}
            >
              <span className="n">{d.day}</span>
              {h ? (
                <span className="fa-cal-dots" aria-hidden="true">
                  {h.balance ? <i className="fa-cal-dot bal" /> : null}
                  {h.movement ? <i className="fa-cal-dot ins" /> : null}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
      <div className="fa-cal-key" aria-hidden="true">
        <i className="fa-cal-dot bal" /> balance stated <i className="fa-cal-dot ins" /> movement
      </div>
      <div className="fa-cal-jump">
        <span className="fa-lbl">jump to</span>
        <span className="fa-cal-chips">
          {dates
            .filter((d) => d.date <= today)
            .map((d) => (
              <button
                type="button"
                key={d.date}
                className={`fa-cal-chip${d.balance ? ' bal' : ''}${value === d.date ? ' on' : ''}`}
                title={formatOfficerDate(d.date) ?? undefined}
                aria-label={`as of ${formatOfficerDate(d.date)}`}
                data-testid={`rebuild-asof-jump-${d.date}`}
                onClick={() => onPick(d.date)}
              >
                {shortDate(d.date, today)}
              </button>
            ))}
          <button
            type="button"
            className="fa-cal-chip"
            data-testid="rebuild-asof-jump-today"
            onClick={onToday}
          >
            today
          </button>
        </span>
      </div>
    </div>
  )
}
